import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Search, MapPin, Filter, Star, Phone, Globe, ChevronRight, 
  Stethoscope, Scissors, Droplets, GraduationCap, Home, Hotel,
  Footprints, Heart, UtensilsCrossed, ShoppingBag, Coffee, Camera,
  Shield, Truck, UserCheck, MoreHorizontal, X, Building2, ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Icon mapping
const CATEGORY_ICONS = {
  vet: Stethoscope,
  groomer: Scissors,
  dog_wash: Droplets,
  trainer: GraduationCap,
  daycare: Home,
  boarding: Hotel,
  walker: Footprints,
  breeder: Heart,
  pet_food: UtensilsCrossed,
  pet_shop: ShoppingBag,
  cafe: Coffee,
  photographer: Camera,
  insurance: Shield,
  transport: Truck,
  sitter: UserCheck,
  other: MoreHorizontal
};

const PupDirectory = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState({});
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedState, setSelectedState] = useState(searchParams.get('state') || '');
  const [selectedArea, setSelectedArea] = useState(searchParams.get('area') || '');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchLocations();
  }, []);

  useEffect(() => {
    fetchBusinesses();
    // Update URL params
    const params = new URLSearchParams();
    if (selectedCategory) params.set('category', selectedCategory);
    if (selectedState) params.set('state', selectedState);
    if (selectedArea) params.set('area', selectedArea);
    if (searchQuery) params.set('q', searchQuery);
    setSearchParams(params);
  }, [selectedCategory, selectedState, selectedArea, searchQuery]);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/puppoto/directory/categories`);
      const data = await res.json();
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/puppoto/directory/locations`);
      const data = await res.json();
      setLocations(data.locations || {});
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const fetchBusinesses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedState) params.append('state', selectedState);
      if (selectedArea) params.append('area', selectedArea);
      if (searchQuery) params.append('search', searchQuery);
      
      const res = await fetch(`${API_URL}/api/puppoto/directory/businesses?${params}`);
      const data = await res.json();
      setBusinesses(data.businesses || []);
      setTotalCount(data.total || 0);
    } catch (err) {
      console.error('Error fetching businesses:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedState('');
    setSelectedArea('');
    setSearchQuery('');
  };

  const getCategoryIcon = (categoryId) => {
    const IconComponent = CATEGORY_ICONS[categoryId] || Building2;
    return <IconComponent className="w-5 h-5" />;
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || categoryId;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white">
        <div className="container mx-auto px-4 py-8">
          <button 
            onClick={() => navigate('/puppoto')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Puppoto
          </button>
          
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Pet Services Directory</h1>
          <p className="text-white/90">Find trusted vets, groomers, trainers, and more near you</p>
          
          {/* Search Bar */}
          <div className="mt-6 flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search businesses, services..."
                className="w-full pl-12 pr-4 py-3 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
            </div>
            <Button 
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className="bg-white/20 border-white/30 text-white hover:bg-white/30"
            >
              <Filter className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Filters Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden mb-6"
            >
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-gray-800">Filters</h3>
                  <button onClick={clearFilters} className="text-orange-500 text-sm hover:underline">
                    Clear all
                  </button>
                </div>
                
                <div className="grid md:grid-cols-3 gap-4">
                  {/* Category Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    >
                      <option value="">All Categories</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* State Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                    <select
                      value={selectedState}
                      onChange={(e) => {
                        setSelectedState(e.target.value);
                        setSelectedArea('');
                      }}
                      className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    >
                      <option value="">All States</option>
                      {Object.entries(locations).map(([code, loc]) => (
                        <option key={code} value={code}>{loc.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Area Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Area</label>
                    <select
                      value={selectedArea}
                      onChange={(e) => setSelectedArea(e.target.value)}
                      disabled={!selectedState}
                      className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:bg-gray-100"
                    >
                      <option value="">All Areas</option>
                      {selectedState && locations[selectedState]?.areas.map(area => (
                        <option key={area} value={area}>{area}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Active Filters */}
        {(selectedCategory || selectedState || selectedArea) && (
          <div className="flex flex-wrap gap-2 mb-4">
            {selectedCategory && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                {getCategoryName(selectedCategory)}
                <X className="w-4 h-4 cursor-pointer" onClick={() => setSelectedCategory('')} />
              </span>
            )}
            {selectedState && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                {locations[selectedState]?.name}
                <X className="w-4 h-4 cursor-pointer" onClick={() => { setSelectedState(''); setSelectedArea(''); }} />
              </span>
            )}
            {selectedArea && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                {selectedArea}
                <X className="w-4 h-4 cursor-pointer" onClick={() => setSelectedArea('')} />
              </span>
            )}
          </div>
        )}

        {/* Category Quick Links (when no filters) */}
        {!selectedCategory && !selectedState && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Browse by Category</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {categories.slice(0, 12).map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className="flex flex-col items-center gap-2 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all hover:scale-105 border border-orange-100"
                >
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-500">
                    {getCategoryIcon(cat.id)}
                  </div>
                  <span className="text-sm font-medium text-gray-700 text-center">{cat.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results Count */}
        <div className="flex justify-between items-center mb-4">
          <p className="text-gray-600">
            {loading ? 'Searching...' : `${totalCount} businesses found`}
          </p>
          <Button
            onClick={() => navigate('/puppoto/directory/submit')}
            className="bg-gradient-to-r from-orange-500 to-amber-500 text-white"
          >
            List Your Business
          </Button>
        </div>

        {/* Business Listings */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                <div className="h-40 bg-gray-200 rounded-lg mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : businesses.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl">
            <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No businesses found</h3>
            <p className="text-gray-500 mb-6">Be the first to list in this category!</p>
            <Button
              onClick={() => navigate('/puppoto/directory/submit')}
              className="bg-gradient-to-r from-orange-500 to-amber-500 text-white"
            >
              List Your Business
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {businesses.map(biz => (
              <motion.div
                key={biz.business_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all overflow-hidden border border-orange-100"
              >
                {/* Business Image/Logo */}
                <div className="h-40 bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center relative">
                  {biz.logo_url ? (
                    <img src={biz.logo_url} alt={biz.business_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-orange-400">
                      {getCategoryIcon(biz.category)}
                    </div>
                  )}
                  {biz.is_sponsor && (
                    <span className="absolute top-2 right-2 px-2 py-1 bg-amber-500 text-white text-xs font-bold rounded-full">
                      {biz.sponsor_tier?.toUpperCase() || 'SPONSOR'}
                    </span>
                  )}
                  {biz.featured && !biz.is_sponsor && (
                    <span className="absolute top-2 right-2 px-2 py-1 bg-orange-500 text-white text-xs font-bold rounded-full">
                      FEATURED
                    </span>
                  )}
                </div>
                
                {/* Business Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-semibold text-gray-800 line-clamp-1">{biz.business_name}</h3>
                    {biz.is_verified && (
                      <span className="text-green-500" title="Verified">✓</span>
                    )}
                  </div>
                  
                  <p className="text-sm text-orange-600 mb-2">{getCategoryName(biz.category)}</p>
                  
                  <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
                    <MapPin className="w-4 h-4" />
                    <span>{biz.location?.area}, {biz.location?.state_name}</span>
                  </div>
                  
                  {biz.description && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">{biz.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      {biz.phone && (
                        <a href={`tel:${biz.phone}`} className="text-gray-400 hover:text-orange-500">
                          <Phone className="w-4 h-4" />
                        </a>
                      )}
                      {biz.website && (
                        <a href={biz.website} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-orange-500">
                          <Globe className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                    <button 
                      onClick={() => navigate(`/puppoto/directory/business/${biz.business_id}`)}
                      className="text-orange-500 text-sm font-medium flex items-center gap-1 hover:underline"
                    >
                      View Details <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PupDirectory;
